import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Unlock,
  Search,
  RefreshCw,
  Users,
  Zap,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Wrench,
  X
} from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import DashboardHeader from '../components/DashboardHeader';
import DashboardCard from '../components/DashboardCard';
import Input from '../components/Input';
import Button from '../components/Button';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { PageSkeleton } from '../components/Skeleton';

const Groups = () => {
  const { socket } = useSocket();
  const [groups, setGroups] = useState([]);
  const [lockers, setLockers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedLockers, setSelectedLockers] = useState([]);
  const [lockerPickerFilter, setLockerPickerFilter] = useState('');
  const [bulkOperationResults, setBulkOperationResults] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, tone, label, action }
  const [busy, setBusy] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  const fetchData = useCallback(async (initial = false) => {
    try {
      if (!initial) setRefreshing(true);
      const [groupsRes, lockersRes] = await Promise.all([
        axios.get('/api/groups'),
        axios.get('/api/lockers')
      ]);
      setGroups(groupsRes.data);
      setLockers(lockersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch groups');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);

    if (socket) {
      socket.on('locker-group-added', (newGroup) => {
        setGroups(prev => (prev.some(g => g.id === newGroup.id) ? prev : [...prev, newGroup]));
      });
      socket.on('locker-group-updated', (updatedGroup) => {
        setGroups(prev => prev.map(g => (g.id === updatedGroup.id ? { ...g, ...updatedGroup } : g)));
      });
      socket.on('locker-group-deleted', (deletedGroup) => {
        setGroups(prev => prev.filter(g => g.id !== deletedGroup.id));
      });
      // Keep available/occupied counts live as lockers change elsewhere
      socket.on('locker-updated', (updatedLocker) => {
        setLockers(prev => prev.map(l => (l.id === updatedLocker.id ? updatedLocker : l)));
      });
      socket.on('locker-created', (newLocker) => {
        setLockers(prev => (prev.some(l => l.id === newLocker.id) ? prev : [...prev, newLocker]));
      });
      socket.on('locker-deleted', (deletedLocker) => {
        setLockers(prev => prev.filter(l => l.id !== deletedLocker.id));
      });
      socket.on('bulk-operation-completed', (operationResult) => {
        setBulkOperationResults(operationResult);
        setTimeout(() => setBulkOperationResults(null), 10000);
      });
    }

    return () => {
      if (socket) {
        socket.off('locker-group-added');
        socket.off('locker-group-updated');
        socket.off('locker-group-deleted');
        socket.off('locker-updated');
        socket.off('locker-created');
        socket.off('locker-deleted');
        socket.off('bulk-operation-completed');
      }
    };
  }, [socket, fetchData]);

  const handleAddGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/locker-groups', {
        ...formData,
        locker_ids: selectedLockers
      });
      setFormData({ name: '', description: '', color: '#3B82F6' });
      setSelectedLockers([]);
      setShowAddModal(false);
      toast.success(`Group "${formData.name}" created`);
    } catch (error) {
      console.error('Error adding group:', error);
      toast.error(error.response?.data?.error || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/locker-groups/${editingGroup.id}`, {
        ...formData,
        locker_ids: selectedLockers
      });
      setFormData({ name: '', description: '', color: '#3B82F6' });
      setSelectedLockers([]);
      setEditingGroup(null);
      toast.success(`Group "${formData.name}" updated`);
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error(error.response?.data?.error || 'Failed to update group');
    }
  };

  const handleDeleteGroup = (group) => {
    setConfirm({
      title: `Delete group "${group.name}"?`,
      message: 'Lockers in the group are not deleted; they just become ungrouped.',
      tone: 'danger',
      label: 'Delete',
      action: async () => {
        setBusy(true);
        try {
          await axios.delete(`/api/locker-groups/${group.id}`);
          toast.success(`Group "${group.name}" deleted`);
        } catch (error) {
          console.error('Error deleting group:', error);
          toast.error(error.response?.data?.error || 'Failed to delete group');
        } finally {
          setBusy(false);
          setConfirm(null);
        }
      }
    });
  };

  const runBulkOperation = async (groupId, operation) => {
    setBusy(true);
    try {
      let endpoint;
      let payload = {};
      switch (operation) {
        case 'unlock':
          endpoint = `/api/locker-groups/${groupId}/bulk-unlock`;
          break;
        case 'maintenance':
          endpoint = `/api/locker-groups/${groupId}/bulk-maintenance`;
          payload = { enable: true };
          break;
        case 'normal':
          endpoint = `/api/locker-groups/${groupId}/bulk-maintenance`;
          payload = { enable: false };
          break;
        default:
          throw new Error('Unknown operation');
      }
      await axios.post(endpoint, payload);
      toast.success(
        operation === 'unlock' ? 'Bulk unlock started' :
        operation === 'maintenance' ? 'Group set to maintenance' :
        'Group back in service'
      );
    } catch (error) {
      console.error('Error performing bulk operation:', error);
      toast.error(error.response?.data?.error || 'Failed to perform bulk operation');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const confirmBulkOperation = (group, operation) => {
    const lockerCount = getGroupLockers(group).length;
    const copy = {
      unlock: {
        title: `Unlock all ${lockerCount} lockers in "${group.name}"?`,
        message: 'Every locker in the group pulses open.',
        tone: 'warning',
        label: 'Unlock all'
      },
      maintenance: {
        title: `Put "${group.name}" in maintenance?`,
        message: `${lockerCount} lockers stop accepting assignments.`,
        tone: 'warning',
        label: 'Maintenance'
      },
      normal: {
        title: `Return "${group.name}" to service?`,
        message: `${lockerCount} lockers become available again.`,
        tone: 'primary',
        label: 'Back in service'
      }
    }[operation];
    setConfirm({ ...copy, action: () => runBulkOperation(group.id, operation) });
  };

  const openAddModal = () => {
    setFormData({ name: '', description: '', color: '#3B82F6' });
    setSelectedLockers([]);
    setLockerPickerFilter('');
    setShowAddModal(true);
  };

  const openEditModal = (group) => {
    setFormData({
      name: group.name || '',
      description: group.description || '',
      color: group.color || '#3B82F6'
    });
    setSelectedLockers(Array.isArray(group.locker_ids) ? group.locker_ids : []);
    setLockerPickerFilter('');
    setEditingGroup(group);
  };

  const toggleLockerSelection = (lockerId) => {
    setSelectedLockers(prev =>
      prev.includes(lockerId)
        ? prev.filter(id => id !== lockerId)
        : [...prev, lockerId]
    );
  };

  const getGroupLockers = (group) => {
    const lockerIds = Array.isArray(group.locker_ids) ? group.locker_ids : [];
    return lockers.filter(locker => lockerIds.includes(locker.id));
  };

  const getAvailableLockers = () => {
    const usedLockerIds = new Set();
    groups.forEach(group => {
      if (editingGroup && group.id === editingGroup.id) return;
      const lockerIds = Array.isArray(group.locker_ids) ? group.locker_ids : [];
      lockerIds.forEach(id => usedLockerIds.add(id));
    });
    return lockers.filter(locker => !usedLockerIds.has(locker.id));
  };

  const filteredGroups = groups.filter(group =>
    (group.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (group.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <PageSkeleton cards={6} />;
  }

  const pickerLockers = getAvailableLockers()
    .concat(editingGroup ? getGroupLockers(editingGroup) : [])
    .filter(l => !lockerPickerFilter || (l.name || '').toLowerCase().includes(lockerPickerFilter.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }));

  return (
    <div className="p-6">
      <DashboardHeader title="Locker Groups" subtitle="Organize lockers into groups for bulk management" />

      {/* Bulk Operation Results */}
      {bulkOperationResults && (
        <DashboardCard className="mb-6 p-4">
          <div className="flex items-start space-x-3">
            <Zap className="w-5 h-5 text-cyan-400 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-white">
                Bulk Operation Completed: {bulkOperationResults.groupName}
              </h3>
              <p className="text-sm text-cyan-200 mt-1">
                Operation: {bulkOperationResults.operation} |
                Successful: {bulkOperationResults.results.length} |
                Failed: {bulkOperationResults.errors.length}
              </p>
              {bulkOperationResults.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-danger-400">Errors:</p>
                  <ul className="text-xs text-danger-400 mt-1">
                    {bulkOperationResults.errors.map((error, index) => (
                      <li key={index}>
                        {error.lockerName || error.lockerId}: {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <Button variant="secondary" className="text-cyan-200 hover:text-white" onClick={() => setBulkOperationResults(null)}>
              <XCircle className="w-5 h-5" />
            </Button>
          </div>
        </DashboardCard>
      )}

      {/* Header Actions */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-cyan-400" />
            <Input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="secondary" className="flex items-center space-x-2" onClick={() => fetchData(false)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
          <Button variant="primary" className="flex items-center space-x-2" onClick={openAddModal}>
            <Plus className="w-5 h-5" />
            <span>New Group</span>
          </Button>
        </div>
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGroups.map((group) => {
          const groupLockers = getGroupLockers(group);
          const availableCount = groupLockers.filter(l => l.status === 'available').length;
          const occupiedCount = groupLockers.filter(l => l.status === 'occupied').length;

          return (
            <DashboardCard key={group.id}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: group.color }}
                  ></div>
                  <div>
                    <h3 className="font-semibold text-white">{group.name}</h3>
                    {group.description && (
                      <p className="text-sm text-cyan-200">{group.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="secondary" className="text-cyan-200 hover:text-white" onClick={() => openEditModal(group)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="danger" className="text-danger-400 hover:text-white" onClick={() => handleDeleteGroup(group)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-200">{groupLockers.length} lockers</span>
                </div>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-success-400" />
                  <span className="text-success-200">{availableCount} available</span>
                </div>
                <div className="flex items-center space-x-1">
                  <AlertTriangle className="w-4 h-4 text-warning-400" />
                  <span className="text-warning-200">{occupiedCount} occupied</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button variant="success" className="flex items-center justify-center space-x-2" onClick={() => confirmBulkOperation(group, 'unlock')}>
                  <Unlock className="w-4 h-4" />
                  <span>Unlock All</span>
                </Button>
                <Button variant="warning" className="flex items-center justify-center space-x-2" onClick={() => {
                  const operation = groupLockers.some(l => l.status === 'maintenance') ? 'normal' : 'maintenance';
                  confirmBulkOperation(group, operation);
                }}>
                  <Wrench className="w-4 h-4" />
                  <span>
                    {groupLockers.some(l => l.status === 'maintenance')
                      ? 'Remove Maintenance'
                      : 'Maintenance'
                    }
                  </span>
                </Button>
              </div>
            </DashboardCard>
          );
        })}
      </div>

      {groups.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No groups yet</h3>
          <p className="text-cyan-200 mb-6">Create your first locker group to get started</p>
          <Button variant="primary" className="inline-flex items-center space-x-2" onClick={openAddModal}>
            <Plus className="w-5 h-5" />
            <span>Create Group</span>
          </Button>
        </div>
      )}
      {groups.length > 0 && filteredGroups.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No matches</h3>
          <p className="text-cyan-200 mb-6">No groups match "{searchTerm}"</p>
          <Button variant="secondary" className="inline-flex items-center space-x-2" onClick={() => setSearchTerm('')}>
            <X className="w-4 h-4" />
            <span>Clear search</span>
          </Button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal open={showAddModal || !!editingGroup} onClose={() => { setShowAddModal(false); setEditingGroup(null); }} hideDefaultClose>
        <h2 className="text-xl font-bold mb-4 text-white">
          {editingGroup ? 'Edit Group' : 'Create New Group'}
        </h2>
        <form onSubmit={editingGroup ? handleUpdateGroup : handleAddGroup}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Group Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                required
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Description
              </label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-cyan-200 mb-1">
                Color
              </label>
              <Input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-16 h-10 p-0 border-none bg-transparent"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-cyan-200">
                  Lockers ({selectedLockers.length} selected)
                </label>
                <input
                  type="text"
                  placeholder="Filter..."
                  value={lockerPickerFilter}
                  onChange={(e) => setLockerPickerFilter(e.target.value)}
                  className="bg-white/[0.06] text-footnote text-white placeholder:text-muted rounded-lg px-2.5 py-1 outline-none focus:ring-1 focus:ring-primary-400 w-28"
                />
              </div>
              <div className="grid grid-cols-3 gap-2 max-h-44 overflow-y-auto pr-1">
                {pickerLockers.map(locker => (
                  <button
                    type="button"
                    key={locker.id}
                    onClick={() => toggleLockerSelection(locker.id)}
                    className={`py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedLockers.includes(locker.id)
                        ? 'bg-primary-500/30 text-primary-200 ring-1 ring-primary-400'
                        : 'bg-white/[0.06] text-secondary hover:bg-white/[0.1]'
                    }`}
                  >
                    {locker.name}
                  </button>
                ))}
                {pickerLockers.length === 0 && (
                  <p className="col-span-3 text-caption text-muted py-3 text-center">No lockers available</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="secondary" type="button" onClick={() => { setShowAddModal(false); setEditingGroup(null); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingGroup ? 'Update Group' : 'Create Group'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.label}
        tone={confirm?.tone}
        busy={busy}
      />
    </div>
  );
};

export default Groups;
